import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { services } from '@/services';
import type { Product } from '@/types';
import { useDebounce } from '@/hooks/useDebounce';
import { formatPrice } from '@/lib/format';
import { ProductVisual } from '@/components/common/ProductVisual';
import { Input } from '@/components/ui/Form';

export function SearchBox({ className, autoFocus, onDone }: { className?: string; autoFocus?: boolean; onDone?: () => void }) {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Product[]>([]);
  const dq = useDebounce(q, 200);
  const root = useRef<HTMLFormElement>(null);

  useEffect(() => {
    let live = true;
    services.catalog.suggest(dq).then((r) => live && setResults(r)).catch(() => live && setResults([]));
    return () => { live = false; };
  }, [dq]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    setOpen(false);
    nav(`/search?q=${encodeURIComponent(q.trim())}`);
    onDone?.();
  };
  const go = (slug: string) => {
    setOpen(false); setQ('');
    nav(`/products/${slug}`);
    onDone?.();
  };

  return (
    <form ref={root} onSubmit={submit} role="search" className={className}>
      <div className="relative">
        <Input
          type="search" value={q} autoFocus={autoFocus} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder="Search products…" aria-label="Search products" className="rounded-full pr-12"
          leading={<Search className="size-4" />}
        />
        {open && results.length > 0 && (
          <ul className="anim-pop absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-border bg-card shadow-pop">
            {results.map((p) => (
              <li key={p.id}>
                <button type="button" onClick={() => go(p.slug)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted">
                  <div className="size-10 shrink-0 overflow-hidden rounded-lg"><ProductVisual icon={p.icon} hue={p.hue} image={p.images[0]} /></div>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{p.name}</span>
                  <span className="text-sm font-semibold">{formatPrice(p.price)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </form>
  );
}
